import * as Sentry from '@sentry/vue';
import { onCLS, onFCP, onFID, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { maskOrderStatusLinks } from '~/utils/orderStatusLinks';

let webVitalsStarted = false;

interface PendingMeasurement {
  name: string;
  value: number;
  unit: string;
  receivedAt: number;
  element?: string;
}

const pendingMeasurements: PendingMeasurement[] = [];

const isPlainObject = (value: object) => {
  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
};

/**
 * Убирает хэши приватных заказов (/order-status/<hash>) из события Sentry:
 * ссылка на статус заказа — токен доступа, ей не место во внешнем сервисе.
 */
const maskPrivateLinks = (value: any, seen = new WeakSet<object>(), depth = 0): any => {
  if (typeof value === 'string') {
    return maskOrderStatusLinks(value);
  }

  if (!value || typeof value !== 'object' || depth > 8 || seen.has(value)) {
    return value;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      value[index] = maskPrivateLinks(item, seen, depth + 1);
    });

    return value;
  }

  if (isPlainObject(value)) {
    Object.keys(value).forEach((key) => {
      value[key] = maskPrivateLinks(value[key], seen, depth + 1);
    });
  }

  return value;
};

/** Селектор элемента для INP (entries[0].target, без long-animation-frame) */
const getElementSelector = (node: Node | null | undefined, maxLen = 100): string => {
  if (!node || node.nodeType !== 1) return '';
  try {
    let sel = '';
    let n: Node | null = node;
    while (n && n.nodeType !== 9) {
      const el = n as Element;
      const part = el.id ? `#${el.id}` : el.tagName.toLowerCase() + (el.className && typeof el.className === 'string' ? '.' + String(el.className).trim().split(/\s+/).slice(0, 2).join('.') : '');
      if (sel.length + part.length > maxLen - 1) return sel || part;
      sel = sel ? part + '>' + sel : part;
      if (el.id) break;
      n = el.parentNode;
    }
    return sel;
  } catch {
    return '';
  }
};

const sendWebVitalToSentry = (metric: Metric) => {
  const unit = metric.name === 'CLS' ? 'none' : 'millisecond';
  const name = metric.name.toLowerCase();
  const entries = name === 'inp' ? (metric as { entries?: Array<{ target?: Node | null }> }).entries ?? [] : [];
  const entryWithTarget = entries.find((e) => e && 'target' in e && (e as { target?: Node | null }).target);
  const element = entryWithTarget ? getElementSelector((entryWithTarget as { target?: Node | null }).target ?? undefined) : undefined;

  pendingMeasurements.push({
    name,
    value: metric.value,
    unit,
    receivedAt: Date.now() / 1000,
    ...(name === 'inp' && element && { element })
  });

  const metricsApi = (Sentry as any).metrics;
  if (metricsApi?.distribution) {
    const tags: Record<string, string> = { rating: metric.rating, webVital: metric.name };
    if (name === 'inp' && element) tags.interaction_target = element;
    metricsApi.distribution(`web-vital.${name}`, metric.value, { unit, tags });
  }

};

const startWebVitalsCollection = () => {
  if (webVitalsStarted || typeof window === 'undefined') {
    return;
  }

  webVitalsStarted = true;

  onCLS(sendWebVitalToSentry, { reportAllChanges: true });
  onINP(sendWebVitalToSentry, { reportAllChanges: true }); // INP при каждом взаимодействии, не только при скрытии
  [onFCP, onFID, onLCP, onTTFB].forEach((trackMetric) => {
    trackMetric(sendWebVitalToSentry);
  });
};

export default defineNuxtPlugin({
  name: 'sentry',
  enforce: 'pre',
  setup (nuxtApp) {
    const router = useRouter();
    const { public: { sentry, baseApiUrl } } = useRuntimeConfig();

    if (!sentry?.dsn) {
      console.warn('[Sentry] Не инициализирован: нет NUXT_PUBLIC_SENTRY_DSN.');
      return;
    }

    const traceTargets = ['localhost'];
    if (baseApiUrl && typeof baseApiUrl === 'string' && !baseApiUrl.includes('undefined')) {
      traceTargets.push(baseApiUrl);
    }

    Sentry.init({
      app: nuxtApp.vueApp,
      dsn: sentry.dsn,
      environment: sentry.environment,

      ignoreErrors: [
        /\/_nuxt\/builds\//,
        /Failed to fetch dynamically imported module.*\/_nuxt\//,
        /error loading dynamically imported module:.*\/_nuxt\//,
        /Unable to preload CSS for .*\/_nuxt\//,
        'Importing a module script failed.',
        /Loading chunk \d+ failed/,
        /Failed to load resource/,
        /ChunkLoadError/,
        /Cannot read propert(?:y|ies) of (?:undefined|null) \(reading 'removeEventListener'\)/,
        /undefined is not an object \(evaluating/,
        /null is not an object \(evaluating/,
        /Cannot read propert(?:y|ies) of undefined \(reading '_sentryRootSpan'\)/,
        "Cannot read properties of undefined (reading '_sentryRootSpan')",
        /Cannot read propert(?:y|ies) of null \(reading 'namespaceURI'\)/,
        /^ym is not defined$/,
        /crypto\.randomUUID is not a function/
      ],

      integrations: (defaultIntegrations) => [
        ...defaultIntegrations.filter((integration) => integration.name !== 'FunctionToString'),
        Sentry.browserTracingIntegration({
          router,
          idleTimeout: 30000,
          finalTimeout: 60000,
          enableInp: true
        })
      ],

      tracesSampleRate: 1.0,

      tracePropagationTargets: traceTargets,

      beforeSendTransaction (event) {
        const txStart = event.start_timestamp ?? 0;
        const txEnd = event.timestamp ?? Date.now() / 1000;
        const toApply = pendingMeasurements.filter((m) => {
          const margin = m.name === 'inp' ? 15 : 2; // INP часто приходит при скрытии страницы — расширенное окно
          return m.receivedAt >= txStart - margin && m.receivedAt <= txEnd + margin;
        });
        if (toApply.length > 0) {
          event.measurements = event.measurements ?? {};
          let bestInpElement: string | undefined;
          for (const m of toApply) {
            const existing = event.measurements[m.name];
            // INP: берём максимальное значение (худшее взаимодействие)
            const shouldSet = !existing || (m.name === 'inp' && m.value > existing.value);
            if (shouldSet) {
              (event.measurements as Record<string, { value: number; unit: string }>)[m.name] = { value: m.value, unit: m.unit };
              if (m.name === 'inp' && m.element) bestInpElement = m.element;
            }
          }
          if (bestInpElement) {
            event.tags = event.tags ?? {};
            (event.tags as Record<string, string>)['inp.interaction_target'] = bestInpElement.slice(0, 200);
            event.contexts = event.contexts ?? {};
            (event.contexts as Record<string, unknown>)['inp'] = { interaction_target: bestInpElement };
          }
          const appliedNames = new Set(toApply.map(m => `${m.name}:${m.receivedAt}`));
          for (let i = pendingMeasurements.length - 1; i >= 0; i--) {
            const key = `${pendingMeasurements[i].name}:${pendingMeasurements[i].receivedAt}`;
            if (appliedNames.has(key)) pendingMeasurements.splice(i, 1);
          }
        }
        return maskPrivateLinks(event);
      },

      beforeSend (event) {
        return maskPrivateLinks(event);
      },

      beforeBreadcrumb (breadcrumb) {
        return maskPrivateLinks(breadcrumb);
      }
    });

    startWebVitalsCollection();
  }
});
